import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Provider } from "react-redux";
import { createGlobalStyle } from "styled-components";
import { ConfigProvider, App as AntApp } from "antd";
import Login from "./pages/auth/Login";
import VerificationCode from "./pages/auth/VerificationCode";
import CompanySelection from "./pages/auth/CompanySelection";
import Dashboard from "./pages/dashboard/Dashboard";
import store, { RootState, useAppSelector, useAppDispatch } from "./store";
import UserManagement from "./pages/user/UserManagement";
import MainLayout from "./layouts/MainLayout";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import Agents from "./pages/agents/Agents";
import AgentDetail from "./pages/agents/AgentDetail";
import Outbound from "./pages/outbound/Outbound";
import Inbound from "./pages/inbound/Inbound";
import ReportPage from "./pages/reports/Report";
import ReportDetail from "./pages/reports/ReportDetail";
import Profile from "./pages/profile/Profile";
import CampaignDetail from "./pages/outbound/CampaignDetail";
import Settings from "./pages/settings/Settings";
import { ROLES } from "./models/user.model";
import GoogleCallback from "./pages/auth/GoogleCallback";
import ManagePhoneNumber from "./pages/settings/ManagePhoneNumber";
import { getUserInfor } from "./store/actions/auth.action";
import { initializeLanguageFromFreshData } from "./utils/languageUtils";
import i18n from "./i18n";

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: #f5f5f5;
  }

  /* Override Ant Design font family */
  .ant-btn,
  .ant-input,
  .ant-select,
  .ant-table,
  .ant-modal,
  .ant-card,
  .ant-form,
  .ant-typography,
  .ant-dropdown,
  .ant-menu,
  .ant-tabs,
  .ant-pagination,
  .ant-spin,
  .ant-message,
  .ant-notification,
  .ant-tooltip,
  .ant-popover,
  .ant-drawer,
  .ant-skeleton,
  .ant-alert,
  .ant-badge,
  .ant-tag,
  .ant-progress,
  .ant-slider,
  .ant-switch,
  .ant-checkbox,
  .ant-radio,
  .ant-rate,
  .ant-upload,
  .ant-transfer,
  .ant-tree,
  .ant-cascader,
  .ant-date-picker,
  .ant-time-picker,
  .ant-calendar,
  .ant-picker,
  .ant-mentions,
  .ant-auto-complete,
  .ant-cascader-picker,
  .ant-tree-select,
  .ant-mentions-dropdown,
  .ant-select-dropdown,
  .ant-cascader-menus,
  .ant-picker-dropdown,
  .ant-calendar-picker-container,
  .ant-time-picker-panel,
  .ant-mentions-dropdown-menu,
  .ant-select-tree,
  .ant-select-tree-dropdown,
  .ant-cascader-dropdown,
  .ant-tree-dropdown,
  .ant-mentions-dropdown-menu-item,
  .ant-select-item,
  .ant-select-tree-node-content-wrapper,
  .ant-cascader-menu-item,
  .ant-tree-node-content-wrapper,
  .ant-mentions-dropdown-menu-item-active,
  .ant-select-item-option-active,
  .ant-select-tree-node-content-wrapper:hover,
  .ant-cascader-menu-item:hover,
  .ant-tree-node-content-wrapper:hover,
  .ant-mentions-dropdown-menu-item-selected,
  .ant-select-item-option-selected,
  .ant-select-tree-node-content-wrapper.ant-select-tree-node-selected,
  .ant-cascader-menu-item-active,
  .ant-tree-node-content-wrapper.ant-tree-node-selected {
    font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif !important;
  }

  button {
    font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif !important;
  }
`;

const AppContent = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, isRequestedLogin, user } = useAppSelector(
    (state: RootState) => state.auth
  );

  useEffect(() => {
    const initializeApp = async () => {
      if (isAuthenticated) {
        try {
          // Fetch fresh user data first
          await dispatch(getUserInfor()).unwrap();

          // Then initialize language from fresh data
          initializeLanguageFromFreshData(i18n);
        } catch (error) {
          console.error("Failed to get user info:", error);
          // Fallback to localStorage language if API fails
          initializeLanguageFromFreshData(i18n);
        }
      }
    };

    initializeApp();
  }, [dispatch, isAuthenticated]);

  return (
    <Router>
      <GlobalStyle />
      <Routes>
        {!isAuthenticated && (
          <>
            <Route path="/" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
          </>
        )}
        {isAuthenticated && (
          <Route element={<MainLayout />}>
            <Route path="/" element={<Dashboard />} />
            {JSON.parse(localStorage.getItem("user") || "{}")?.role ===
              ROLES.ADMIN && (
              <Route path="/user-management" element={<UserManagement />} />
            )}
            <Route path="/agents" element={<Agents />} />
            <Route path="/agents/:id" element={<AgentDetail />} />
            <Route path="/outbound" element={<Outbound />} />
            <Route path="/inbound" element={<Inbound />} />
            <Route path="/reports" element={<ReportPage />} />
            <Route path="/reports/:id" element={<ReportDetail />} />
            <Route path="/outbound/:id" element={<CampaignDetail />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/settings" element={<Settings />} />
            <Route
              path="/settings/manage-phone-number"
              element={<ManagePhoneNumber />}
            />
          </Route>
        )}
        {isAuthenticated && user?.is_super_admin && (
          <Route path="/company-selection" element={<CompanySelection />} />
        )}
        {isRequestedLogin && (
          <Route path="/verify" element={<VerificationCode />} />
        )}
        <Route path="/oauth2/callback/google" element={<GoogleCallback />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

const App = () => {
  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily:
            "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
        },
      }}
    >
      <AntApp>
        <AppContent />
      </AntApp>
    </ConfigProvider>
  );
};

export default App;
